import {
    useRef,
    useCallback,
    useEffect,
    useLayoutEffect,
    useState,
} from "react";

export const useChatScroll = (messages, loadMoreMessages) => {
    const scrollContainerRef = useRef(null);
    const [containerReady, setContainerReady] = useState(false);

    const setContainerRef = useCallback((node) => {
        if (node) {
            scrollContainerRef.current = node;
            setContainerReady(true); // 🔥 triggers re-run
        }
    }, []);

    const shouldAutoScroll = useRef(true);
    const lastScrollPosition = useRef(0);

    const hasScrolledToBottom = useRef(false);
    const previousMessageCount = useRef(0);

    const isLoadingMoreRef = useRef(false);

    const isAtTopRef = useRef(false);

    const pullStartYRef = useRef(0);
    const isPullingRef = useRef(false);

    const hasTriggeredRef = useRef(false);
    const isMouseDownRef = useRef(false);
    const isInteractingRef = useRef(false);

    const lastMessageIdRef = useRef(null);


    const [pullState, setPullState] = useState({
        pulling: false,
        distance: 0,
        ready: false,
        loading: false,
    });

    // =========================
    // Scroll to bottom helper
    // =========================
    const scrollToBottom = useCallback((behavior = "auto") => {
        const container = scrollContainerRef.current;
        if (!container) return;

        container.scrollTo({
            top: container.scrollHeight,
            behavior,
        });
    }, []);

    // =========================
    // Track user scroll behavior
    // =========================
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;

            const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

            if (isNearBottom) {
                shouldAutoScroll.current = true;
            } else if (scrollTop < lastScrollPosition.current) {
                shouldAutoScroll.current = false;
            }

            lastScrollPosition.current = scrollTop;
        };

        container.addEventListener("scroll", handleScroll, { passive: true });

        return () => {
            container.removeEventListener("scroll", handleScroll);
        };
    }, []);

    // =========================
    // Main message update logic
    // =========================
    useLayoutEffect(() => {
        const el = scrollContainerRef.current;
        if (!el || !containerReady || messages.length === 0) return;

        const lastMsg = messages[messages.length - 1];
        const latestMessageId = lastMsg?.id;

        // FIRST LOAD
        if (!hasScrolledToBottom.current) {
            el.scrollTop = el.scrollHeight;
            hasScrolledToBottom.current = true;
            lastMessageIdRef.current = latestMessageId;
            previousMessageCount.current = messages.length;
            
            // Insurance for initial render
            requestAnimationFrame(() => {
                if (scrollContainerRef.current) {
                    scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
                }
            });
            return;
        }

        if (isLoadingMoreRef.current) return;

        if (latestMessageId !== lastMessageIdRef.current) {
            if (messages.length > previousMessageCount.current) {
                // REAL new message
                if (shouldAutoScroll.current) {
                    el.scrollTo({
                        top: el.scrollHeight,
                        behavior: "smooth",
                    });
                }
            } else {
                // Firebase refresh
                el.scrollTop = el.scrollHeight;
            }
        }

        lastMessageIdRef.current = latestMessageId;
        previousMessageCount.current = messages.length;

    }, [messages, containerReady]);

    // =========================
    // Load more handler
    // =========================
    const handleLoadMore = useCallback(async () => {
        const el = scrollContainerRef.current;
        if (!el) return;

        const prevScrollHeight = el.scrollHeight;
        const prevScrollTop = el.scrollTop;

        isLoadingMoreRef.current = true;

        await loadMoreMessages();

        // small delay to ensure messages are rendered
        requestAnimationFrame(() => {
            const newScrollHeight = el.scrollHeight;
            el.scrollTop = newScrollHeight - prevScrollHeight + prevScrollTop;
            isLoadingMoreRef.current = false;
        });
    }, [loadMoreMessages]);

    // reset loading flag after messages update
    useEffect(() => {
        isLoadingMoreRef.current = false;
    }, [messages]);

    // =========================
    // Track top position
    // =========================
    const handleScroll = useCallback(() => {
        const el = scrollContainerRef.current;
        if (!el) return;

        const isAtTop = el.scrollTop <= 8;
        isAtTopRef.current = isAtTop;

        if (!isAtTop) {
            hasTriggeredRef.current = false;
        }
    }, []);


    // =========================
    // Pull-to-load-more (Touch + Mouse)
    // =========================
    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return;

        let startY = 0;
        const MAX_PULL = 100;

        const disableSelection = (e) => {
            if (isInteractingRef.current) e.preventDefault();
        };

        const disableContextMenu = (e) => {
            if (isInteractingRef.current) e.preventDefault();
        };

        // ================= TOUCH =================
        const handleTouchStart = (e) => {
            if (!isAtTopRef.current) return;

            isInteractingRef.current = true;
            isPullingRef.current = true;

            pullStartYRef.current = e.touches[0].clientY;

            setPullState({
                pulling: true,
                distance: 0,
                ready: false,
                loading: false,
            });
        };

        const handleTouchMove = (e) => {
            if (!isPullingRef.current || !isAtTopRef.current) return;

            let diff = e.touches[0].clientY - pullStartYRef.current;
            if (diff < 0) diff = 0;

            const clamped = Math.min(diff, MAX_PULL);
            const ready = clamped > 70;

            setPullState({
                pulling: true,
                distance: clamped,
                ready,
                loading: false,
            });

            if (ready) hasTriggeredRef.current = true;
        };

        const handleTouchEnd = async () => {
            if (isPullingRef.current && hasTriggeredRef.current) {
                setPullState({
                    pulling: false,
                    distance: 0,
                    ready: false,
                    loading: true,
                });

                await handleLoadMore();
            }

            setPullState({
                pulling: false,
                distance: 0,
                ready: false,
                loading: false,
            });

            isPullingRef.current = false;
            hasTriggeredRef.current = false;
            isInteractingRef.current = false;
        };

        // ================= MOUSE =================
        const handleMouseDown = (e) => {
            if (!isAtTopRef.current) return;

            isInteractingRef.current = true;
            isMouseDownRef.current = true;
            isPullingRef.current = true;

            startY = e.clientY;

            setPullState({
                pulling: true,
                distance: 0,
                ready: false,
                loading: false,
            });
        };

        const handleMouseMove = (e) => {
            if (!isMouseDownRef.current || !isAtTopRef.current) return;

            let diff = e.clientY - startY;
            if (diff < 0) diff = 0;

            const clamped = Math.min(diff, MAX_PULL);
            const ready = clamped > 70;

            setPullState({
                pulling: true,
                distance: clamped,
                ready,
                loading: false,
            });

            if (ready) hasTriggeredRef.current = true;
        };

        const handleMouseUp = async () => {
            if (!isMouseDownRef.current) return;

            isMouseDownRef.current = false;
            isInteractingRef.current = false;

            if (hasTriggeredRef.current) {
                setPullState({
                    pulling: false,
                    distance: 0,
                    ready: false,
                    loading: true,
                });

                await handleLoadMore();
            }

            setPullState({
                pulling: false,
                distance: 0,
                ready: false,
                loading: false,
            });

            hasTriggeredRef.current = false;
        };

        el.addEventListener("mousedown", handleMouseDown);
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);

        el.addEventListener("touchstart", handleTouchStart);
        el.addEventListener("touchmove", handleTouchMove);
        el.addEventListener("touchend", handleTouchEnd);

        el.addEventListener("selectstart", disableSelection);
        el.addEventListener("contextmenu", disableContextMenu);

        return () => {
            el.removeEventListener("mousedown", handleMouseDown);
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);

            el.removeEventListener("touchstart", handleTouchStart);
            el.removeEventListener("touchmove", handleTouchMove);
            el.removeEventListener("touchend", handleTouchEnd);

            el.removeEventListener("selectstart", disableSelection);
            el.removeEventListener("contextmenu", disableContextMenu);
        };
    }, [handleLoadMore]);

    return {
        scrollContainerRef,
        scrollToBottom,
        setContainerRef,
        handleScroll,
        hasScrolledToBottom: hasScrolledToBottom.current,
        pullState,
    };
};